'use client'

import { twMerge } from '@/lib/classname/twMerge'
import { useResponsiveProp, type ResponsiveProp } from '@/lib/device/useBreakpoint'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { useCallbackRef } from '@/lib/react/useCallbackRef'
import { debounce } from '@/lib/time/services/time.helpers'
import { Button } from '@/ui/buttons/button/Button.client'
import { Icon } from '@/ui/icons/icon/Icon'
import {
  Children,
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

const SCROLLBAR_THUMB_WIDTH_MIN = 20 // px

const GAPS = {
  none: 'gap-0',
  sm: 'gap-1',
  md: 'gap-2',
} as const

export type CSSSliderGap = keyof typeof GAPS
export const CSS_SLIDER_GAPS = Object.keys(GAPS) as CSSSliderGap[]

export type CSSSliderProps = {
  gap: CSSSliderGap
  loop?: boolean
  preventArrowOverlap?: boolean
  noScrollbarOnTouchDevices?: boolean
  onNewItemsVisible?: (fromIndex: number, toIndex: number) => void
  children: React.ReactNode
  className?: string
  slidesScrolledPerArrow?: ResponsiveProp<number>
}

const noop = () => {}

export function CSSSlider({
  gap,
  loop = false,
  preventArrowOverlap = false,
  noScrollbarOnTouchDevices = false,
  onNewItemsVisible,
  children,
  className,
  slidesScrolledPerArrow: slidesScrolledPerArrowProp,
}: CSSSliderProps) {
  const slidesScrolledPerArrowResolved = useResponsiveProp(slidesScrolledPerArrowProp ?? 1)
  const slidesScrolledPerArrow =
    slidesScrolledPerArrowProp !== undefined ? slidesScrolledPerArrowResolved : undefined
  const { t } = useTranslation(['CSSSlider'])

  const gapPx = {
    none: 0,
    sm: 8,
    md: 16,
  }[gap]

  const scrollerRef = useRef<HTMLDivElement>(null)
  const scrollbarThumbRef = useRef<HTMLDivElement>(null)

  const [didReachLeftEnd, setDidReachLeftEnd] = useState(true)
  const [didReachRightEnd, setDidReachRightEnd] = useState(false)

  /// -- Scroll handling

  const onNewItemsVisibleRef = useCallbackRef(onNewItemsVisible ?? noop)

  const scrollTimeoutRef = useRef<ReturnType<typeof window.setTimeout> | null>(null)

  const onScrollEnd = useCallback(() => {
    if (!onNewItemsVisibleRef) return

    const scrollerElement = scrollerRef.current
    if (!scrollerElement) return

    // 1. Determine indices where slides are fully visible

    const containerRect = scrollerElement.getBoundingClientRect()
    const visibleIndices: number[] = []

    Array.from(scrollerElement.children).forEach((child, index) => {
      if (child.getAttribute('role') === 'none') return
      const childRect = child.getBoundingClientRect()
      const isFullyVisible =
        childRect.left >= containerRect.left && childRect.right <= containerRect.right

      if (isFullyVisible) {
        visibleIndices.push(index)
      }
    })

    // 2. Notify new visible items

    if (visibleIndices.length > 0) {
      const fromIndex = visibleIndices[0]!
      const toIndex = visibleIndices[visibleIndices.length - 1]!

      onNewItemsVisibleRef(fromIndex, toIndex)
    }
  }, [onNewItemsVisibleRef])

  const handleScroll = useCallback(() => {
    const scrollerElement = scrollerRef.current

    if (!scrollerElement) return

    setDidReachLeftEnd(scrollerElement.scrollLeft <= 0)
    setDidReachRightEnd(
      scrollerElement.scrollLeft + scrollerElement.clientWidth >= scrollerElement.scrollWidth - 1,
    )

    const thumbWidth = Math.max(
      (scrollerElement.clientWidth / scrollerElement.scrollWidth) * scrollerElement.clientWidth,
      SCROLLBAR_THUMB_WIDTH_MIN,
    )
    const maxScrollLeft = scrollerElement.scrollWidth - scrollerElement.clientWidth
    const thumbLeft =
      (scrollerElement.scrollLeft / maxScrollLeft) * (scrollerElement.clientWidth - thumbWidth)

    if (scrollbarThumbRef.current) {
      scrollbarThumbRef.current.style.width = `${thumbWidth}px`
      scrollbarThumbRef.current.style.transform = `translateX(${thumbLeft}px)`
    }

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current)
    }

    scrollTimeoutRef.current = setTimeout(() => {
      onScrollEnd()
    }, 100)
  }, [onScrollEnd])

  /**
   * Emulate scroll on mouse wheel event for better UX on desktop
   */
  useEffect(() => {
    if ('onwheel' in window === false) {
      return
    }

    const scrollerElement = scrollerRef.current
    if (!scrollerElement) return

    const handleWheel = (event: WheelEvent) => {
      // Have to press Shift key to enable horizontal scroll
      if (!event.shiftKey) return

      // If horizontal scroll is more significant than vertical, let the default behavior happen
      if (Math.abs(event.deltaX) >= Math.abs(event.deltaY)) {
        return
      }

      event.preventDefault()

      // Translate vertical mouse wheel scroll to horizontal scroll
      scrollerElement.scrollBy({
        left: event.deltaY,
        behavior: 'smooth',
      })
    }

    scrollerElement.addEventListener('wheel', handleWheel, { passive: false })

    return () => {
      scrollerElement.removeEventListener('wheel', handleWheel)
    }
  }, [])

  useEffect(() => {
    const scrollerElement = scrollerRef.current
    if (!scrollerElement) return

    requestAnimationFrame(handleScroll)

    scrollerElement.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      scrollerElement.removeEventListener('scroll', handleScroll)
    }
  }, [handleScroll])

  /// -- Detect if content is scrollable

  const [canScrollContent, setCanScrollContent] = useState(false)

  useEffect(() => {
    const scrollerElement = scrollerRef.current
    if (!scrollerElement) return

    const refresh = debounce(() => {
      setCanScrollContent(scrollerElement.scrollWidth > scrollerElement.clientWidth)
      handleScroll()
    }, 50)

    refresh() // Initial

    // 1) MutationObserver: detect addition/removal of slides
    const mo = new MutationObserver(() => {
      refresh()
      // Re-observe children to detect size changes
      observeChildren() /* eslint-disable-line @typescript-eslint/no-use-before-define */
    })

    mo.observe(scrollerElement, { childList: true })

    // 2) ResizeObservers for each child (detect size changes)
    let ros: ResizeObserver[] = []

    const observeChildren = () => {
      // Disconnect old observers
      ros.forEach((r) => r.disconnect())
      ros = []
      const slidesElts = [...scrollerElement.children]
      slidesElts.forEach((slideElt) => {
        const ro = new ResizeObserver(refresh)
        ro.observe(slideElt)
        ros.push(ro)
      })
    }

    // Initial children observation
    observeChildren()

    // 3) Window resize and orientation change
    window.addEventListener('resize', refresh)
    window.addEventListener('orientationchange', refresh)

    return () => {
      mo.disconnect()
      ros.forEach((r) => r.disconnect())
      window.removeEventListener('resize', refresh)
      window.removeEventListener('orientationchange', refresh)
    }
  }, [handleScroll])

  const leftArrowRef = useRef<HTMLDivElement>(null)
  const rightArrowRef = useRef<HTMLDivElement>(null)
  const [leftArrowWidth, setLeftArrowWidth] = useState(40)
  const [rightArrowWidth, setRightArrowWidth] = useState(40)

  useEffect(() => {
    function onResize() {
      const leftWidth = leftArrowRef.current?.offsetWidth || 0
      const rightWidth = rightArrowRef.current?.offsetWidth || 0
      setLeftArrowWidth(leftWidth)
      setRightArrowWidth(rightWidth)
    }

    requestAnimationFrame(onResize)

    const resizeObserver = new ResizeObserver(onResize)

    if (leftArrowRef.current) {
      resizeObserver.observe(leftArrowRef.current)
    }

    if (rightArrowRef.current) {
      resizeObserver.observe(rightArrowRef.current)
    }

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  /// -- Previous/Next arrow handlers

  const handleArrowClick = useCallback(
    // eslint-disable-next-line sonarjs/cognitive-complexity
    (direction: 'left' | 'right') => {
      const scrollerElement = scrollerRef.current
      if (!scrollerElement) return

      const left = direction === 'left'
      const right = direction === 'right'

      if (loop && left && didReachLeftEnd) {
        scrollerElement.scrollTo({
          left: scrollerElement.scrollWidth,
          behavior: 'smooth',
        })
      } else if (loop && right && didReachRightEnd) {
        scrollerElement.scrollTo({ left: 0, behavior: 'smooth' })
      } else if (slidesScrolledPerArrow) {
        // Find the active slide index (preferring the first fully visible slide)
        const childrenElements = Array.from(scrollerElement.children).filter(
          (child) => child.getAttribute('role') !== 'none',
        ) as HTMLElement[]

        const scrollerRect = scrollerElement.getBoundingClientRect()
        // Threshold to ignore trailing slides that only bleed in by a few pixels
        const MIN_PEEK_PX = gapPx / 2

        let referenceIndex = -1

        for (const [i, child] of childrenElements.entries()) {
          const rect = child.getBoundingClientRect()

          const isAtLeastPartiallyVisible =
            rect.right > scrollerRect.left + MIN_PEEK_PX &&
            rect.left < scrollerRect.right - MIN_PEEK_PX

          if (isAtLeastPartiallyVisible) {
            // Anchor on the first visible slide to scroll relative to current position
            referenceIndex = i
            break
          }
        }

        const currentRefIndex = Math.max(0, referenceIndex)

        // Calculate target index and scroll
        const targetIndex = left
          ? Math.max(0, currentRefIndex - slidesScrolledPerArrow)
          : Math.min(childrenElements.length - 1, currentRefIndex + slidesScrolledPerArrow)

        const targetChild = childrenElements[targetIndex]

        if (targetChild) {
          scrollerElement.scrollTo({
            left: targetChild.offsetLeft - leftArrowWidth / 2,
            behavior: 'smooth',
          })
        }
      } else {
        scrollerElement.scrollBy({
          left: left ? -scrollerElement.offsetWidth : scrollerElement.offsetWidth,
          behavior: 'smooth',
        })
      }
    },
    [loop, didReachLeftEnd, didReachRightEnd, slidesScrolledPerArrow, leftArrowWidth, gapPx],
  )

  return (
    <div data-identity="slider" className={twMerge(className, 'relative')}>
      {/* -- Slides container */}

      <div
        ref={scrollerRef}
        className={twMerge([
          GAPS[gap],
          'flex snap-x snap-mandatory overflow-x-auto scroll-smooth scrollbar-none',
          canScrollContent && !noScrollbarOnTouchDevices && 'touch:pb-2',
          'transition-all',
        ])}
      >
        {/* -- Slides */}

        {Children.toArray(children)
          .filter((child) =>
            isValidElement<{ className?: string; style?: React.CSSProperties }>(child),
          )
          .map((child, index) => {
            if (!scrollerRef.current) return child

            const isFirstSlide = index === 0
            const isLastSlide = index === Children.count(children) - 1

            const leftOffset = leftArrowWidth / 2
            const rightOffset = rightArrowWidth / 2

            return cloneElement(child, {
              key: child.key,
              style: {
                marginLeft:
                  canScrollContent && isFirstSlide && preventArrowOverlap ? leftOffset : 0,
                marginRight:
                  canScrollContent && isLastSlide && preventArrowOverlap ? rightOffset : 0,
                scrollMarginLeft: canScrollContent ? leftOffset : 0,
                scrollMarginRight: canScrollContent ? rightOffset : 0,
              },
              className: twMerge(
                child.props.className,
                'shrink-0',
                !isLastSlide && 'snap-start',
                isLastSlide && 'snap-end',
              ),
            })
          })}
      </div>

      {/* -- Scrollbar */}

      {canScrollContent && (
        <div
          role="none"
          data-identity="slider-scrollbar-track"
          className={twMerge(
            'pointer-events-none hidden h-[0.125rem] w-full rounded-full bg-[--scrollbar-track-color] touch:block',
            noScrollbarOnTouchDevices && 'touch:hidden',
          )}
        >
          <div
            ref={scrollbarThumbRef}
            data-identity="slider-scrollbar-thumb"
            className="h-full w-1/3 rounded-full bg-[--scrollbar-thumb-color]"
          />
        </div>
      )}

      {/* -- Previous arrow */}

      <div
        ref={leftArrowRef}
        className={twMerge(
          'absolute left-0 top-1/2 z-[1] ml-1 -translate-x-1/2 -translate-y-1/2 transition-opacity duration-150 touch:hidden desktop:ml-0',
          !(canScrollContent && (!didReachLeftEnd || loop)) && 'pointer-events-none opacity-0',
        )}
      >
        <Button
          size="md"
          isRound
          variant="tertiary"
          iconLeft={<Icon.ArrowLeft />}
          onPress={() => handleArrowClick('left')}
          aria-label={t('CSSSlider:actions.previous.ariaLabel')}
        >
          {t('CSSSlider:actions.previous.title')}
        </Button>
      </div>

      {/* -- Next arrow */}

      <div
        ref={rightArrowRef}
        className={twMerge(
          'absolute right-0 top-1/2 z-[1] mr-1 -translate-y-1/2 translate-x-1/2 transition-opacity duration-150 touch:hidden desktop:mr-0',
          !(canScrollContent && (!didReachRightEnd || loop)) && 'pointer-events-none opacity-0',
        )}
      >
        <Button
          size="md"
          isRound
          variant="tertiary"
          iconLeft={<Icon.ArrowRight />}
          onPress={() => handleArrowClick('right')}
          aria-label={t('CSSSlider:actions.next.ariaLabel')}
        >
          {t('CSSSlider:actions.next.title')}
        </Button>
      </div>
    </div>
  )
}
